import axios from 'axios'
import errorHandler from './errorHandler'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  response => {
    return response
  },
  error => {
    const errorInfo = errorHandler.handleApiError(error)
    
    // 401 和 403 都需要退出登录并跳转到登录页
    if (errorInfo.type === errorHandler.ErrorType?.AUTH || 
        (error.response && (error.response.status === 401 || error.response.status === 403))) {
      errorHandler.handleAuthError()
    }
    
    return Promise.reject(error)
  }
)

export default {
  // 首页文案（公开）：三端统一从后端读取，便于集中配置
  home: {
    subtitles: () => api.get('/home/subtitles')
  },

  // 系统配置（公开）：读取三端功能开关，如评论区是否展示
  config: {
    commentVisibility: () => api.get('/config/comment-visibility')
  },

  user: {
    login: (credentials) => api.post('/user/login', credentials),
    register: (userData) => api.post('/user/register', userData),
    loginByPhone: (phoneData) => api.post('/user/login/phone', phoneData),
    loginByWechat: (code) => api.post('/user/login/wechat', { code }),
    sendSmsCode: (data) => api.post('/user/send-sms-code', data),
    sendEmailCode: (data) => api.post('/user/send-email-code', data),
    resetPassword: (data) => api.post('/user/reset-password', data),
    findUsername: (data) => api.post('/user/find-username', data),
    associatePhone: (data) => api.post('/user/associate-phone', data),
    changePassword: (data) => api.post('/user/change-password', data),
    linkPerson: (data) => api.post('/user/link-person', data),
    unlinkPerson: (userId) => api.post(`/user/unlink-person/${userId}`),
    getInfo: (id) => api.post('/user/findById', { id }),
    update: (userData) => api.put('/user', userData),
    updateProfile: (data) => api.put('/user/profile', data),
    // 保存主题色：登录后切换主题时同步到账号，下次登录自动恢复
    updateTheme: (theme) => api.put('/user/theme', { theme }),
    recentActivities: (id) => api.post('/user/recent-activities', { id }),
    checkUsername: (username) => api.post('/user/check-username', { username }),
    checkPhone: (phone) => api.post('/user/check-phone', { phone }),
    checkEmail: (email) => api.post('/user/check-email', { email }),
    // 徽章等级定义 + 当前用户最近登录记录
    badges: () => api.get('/user/badges'),
    loginLogs: () => api.get('/user/login-logs')
  },
  
  person: {
    create: (person) => api.post('/person', person),
    update: (person) => api.put('/person', person),
    delete: (id) => api.delete(`/person/${id}`),
    findById: (id) => api.post('/person/findById', { id }),
    findByCreator: (creatorId) => api.post('/person/findByCreator', { creatorId }),
    findPublicPersons: () => api.post('/person/findPublicPersons'),
    findByFatherId: (fatherId) => api.post('/person/findByFatherId', { fatherId }),
    findByMotherId: (motherId) => api.post('/person/findByMotherId', { motherId }),
    findPersonTree: (personId, maxDepth) => api.post('/person/findPersonTree', { personId, maxDepth }),
    setPublic: (id, isPublic) => api.put(`/person/${id}/public`, {}, { params: { isPublic } }),
    checkViewPermission: (id) => api.post('/person/checkViewPermission', { id }),
    findVisiblePersonsForCurrentUser: () => api.post('/person/findVisiblePersonsForCurrentUser'),
    findEditablePersonsForCurrentUser: () => api.post('/person/findEditablePersonsForCurrentUser')
  },
  
  ownershipTransfer: {
    request: (data) => api.post('/ownership-transfer/request', data),
    getPending: () => api.get('/ownership-transfer/pending'),
    approve: (requestId) => api.post(`/ownership-transfer/approve/${requestId}`),
    reject: (requestId) => api.post(`/ownership-transfer/reject/${requestId}`)
  },
  
  record: {
    create: (record) => api.post('/record', record),
    update: (record) => api.put('/record', record),
    delete: (id) => api.delete(`/record/${id}`),
    findById: (id) => api.post('/record/findById', { id }),
    findByPerson: (personId) => api.post('/record/findByPerson', { personId }),
    findByType: (type) => api.post('/record/findByType', { type }),
    findByCreator: (creatorId) => api.post('/record/findByCreator', { creatorId })
  },
  
  share: {
    create: (share) => api.post('/share', share),
    update: (share) => api.put('/share', share),
    delete: (id) => api.delete(`/share/${id}`),
    findById: (id) => api.post('/share/findById', { id }),
    findBySharer: (sharerId) => api.post('/share/findBySharer', { sharerId }),
    findByRecipient: (recipientId) => api.post('/share/findByRecipient', { recipientId }),
    findByPerson: (personId) => api.post('/share/findByPerson', { personId }),
    findByShareToken: (shareToken) => api.post('/share/findByShareToken', { shareToken }),
    acceptShare: (shareToken) => api.put(`/share/accept/${shareToken}`),
    findPublic: () => api.get('/share/public')
  },
  
  comment: {
    create: (comment) => api.post('/comment/create', comment),
    update: (comment) => api.put(`/comment/${comment.id}`, comment),
    delete: (id) => api.delete(`/comment/${id}`),
    findById: (id) => api.get(`/comment/${id}`),
    findByTarget: (commentType, targetId) => api.get('/comment/list', { params: { commentType, targetId } })
  },
  
  spouseRelation: {
    create: (relation) => api.post('/spouse-relation', relation),
    delete: (id) => api.delete(`/spouse-relation/${id}`)
  },
  
  upload: {
    avatar: (formData) => api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
    media: (formData) => api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  friendship: {
    sendRequest: (data) => api.post('/friendship/send-request', data),
    acceptRequest: (data) => api.post('/friendship/accept-request', data),
    rejectRequest: (data) => api.post('/friendship/reject-request', data),
    removeFriend: (data) => api.post('/friendship/remove-friend', data),
    setFriendLabel: (data) => api.post('/friendship/set-label', data),
    getFriends: (data) => api.post('/friendship/get-friends', data),
    getPendingRequests: (data) => api.post('/friendship/get-pending-requests', data),
    getReceivedRequests: (data) => api.post('/friendship/get-received-requests', data),
    getSentRequests: (data) => api.post('/friendship/get-sent-requests', data),
    getUnreadRequestCount: (data) => api.post('/friendship/get-unread-request-count', data),
    markRequestsAsRead: (data) => api.post('/friendship/mark-requests-as-read', data),
    areFriends: (data) => api.post('/friendship/are-friends', data),
    hasPendingRequest: (data) => api.post('/friendship/has-pending-request', data)
  },

  like: {
    toggle: (data) => api.post('/like/toggle', data),
    info: (data) => api.post('/like/info', data),
    records: (data) => api.post('/like/records', data)
  },

  memorial: {
    perform: (data) => api.post('/memorial/perform', data),
    info: (data) => api.post('/memorial/info', data),
    records: (data) => api.post('/memorial/records', data)
  },

  myRecords: {
    get: () => api.post('/my-records')
  },

  chat: {
    sendMessage: (data) => api.post('/chat/send-message', data),
    getConversation: (data) => api.post('/chat/get-conversation', data),
    getUnreadMessages: (data) => api.post('/chat/get-unread-messages', data),
    markAsRead: (data) => api.post('/chat/mark-as-read', data),
    getUnreadCount: (data) => api.post('/chat/get-unread-count', data)
  },

  milestone: {
    create: (data) => api.post('/milestone/create', data),
    update: (data) => api.post('/milestone/update', data),
    delete: (id) => api.delete(`/milestone/${id}`),
    findById: (id) => api.post('/milestone/findById', { id }),
    findByCreator: (creatorId) => api.post('/milestone/findByCreator', { creatorId }),
    findByPerson: (personId) => api.post('/milestone/findByPerson', { personId }),
    findAll: (userId) => {
      const params = userId ? { userId } : {}
      return api.get('/milestone/findAll', { params })
    },
  },

  ancestralHall: {
    create: (data) => api.post('/ancestral-hall/create', data),
    update: (data) => api.put('/ancestral-hall/update', data),
    delete: (id, userId) => api.delete(`/ancestral-hall/${id}`, { data: { userId } }),
    findById: (id, userId) => api.post('/ancestral-hall/findById', { id, userId }),
    findAll: (data) => api.post('/ancestral-hall/findAll', data || {}),
    findByCreator: (creatorId) => api.post('/ancestral-hall/findByCreator', { creatorId }),
    findByUser: (userId) => api.post('/ancestral-hall/findByUser', { userId }),
    getMembers: (hallId) => api.get(`/ancestral-hall/members/${hallId}`),
    addMember: (data) => api.post('/ancestral-hall/addMember', data),
    removeMember: (data) => api.post('/ancestral-hall/removeMember', data),
    getMemberRole: (data) => api.post('/ancestral-hall/getMemberRole', data),
    isCreator: (data) => api.post('/ancestral-hall/isCreator', data),
    canAddPerson: (data) => api.post('/ancestral-hall/canAddPerson', data),
    addPerson: (data) => api.post('/ancestral-hall/addPerson', data),
    removePerson: (data) => api.post('/ancestral-hall/removePerson', data),
    getHallPersons: (hallId, sortField, sortOrder) => {
      var url = `/ancestral-hall/persons/${hallId}`
      if (sortField) {
        url += `?sortField=${sortField}&sortOrder=${sortOrder}`
      }
      return api.get(url)
    },
    linkUserPerson: (data) => api.post('/ancestral-hall/linkUserPerson', data),
    getHallPersonCount: (hallId) => api.get(`/ancestral-hall/count/${hallId}`)
  },

  // 家族：一律私有，仅成员可查看；管理者(ADMIN)拥有管理权限，普通成员(MEMBER)只读
  family: {
    create: (data) => api.post('/family/create', data),
    update: (data) => api.put('/family/update', data),
    delete: (id, userId) => api.delete(`/family/${id}`, { data: { userId } }),
    findById: (id, userId) => api.post('/family/findById', { id, userId }),
    findByUser: (userId) => api.post('/family/findByUser', { userId }),
    findByCreator: (creatorId) => api.post('/family/findByCreator', { creatorId }),
    // 成员列表/人物列表均为 GET，需带 userId 做成员校验
    getMembers: (familyId, userId) => api.get(`/family/members/${familyId}`, { params: { userId } }),
    addMember: (data) => api.post('/family/addMember', data),
    removeMember: (data) => api.post('/family/removeMember', data),
    // 主动退出家族（成员本人；创建者不可退出）
    leave: (data) => api.post('/family/leave', data),
    setMemberRole: (data) => api.post('/family/setMemberRole', data),
    getMemberRole: (data) => api.post('/family/getMemberRole', data),
    getFamilyPersons: (familyId, userId) => api.get(`/family/persons/${familyId}`, { params: { userId } }),
    addPerson: (data) => api.post('/family/addPerson', data),
    removePerson: (data) => api.post('/family/removePerson', data),
    regenerateInviteCode: (data) => api.post('/family/regenerateInviteCode', data),
    // 公开接口：注册页预览邀请码，无需登录
    previewInviteCode: (inviteCode) => api.post('/family/previewInviteCode', { inviteCode }),
    // 公开接口：人物所在家族（分享详情页展示）
    getPersonFamilies: (personId) => api.get(`/family/personFamilies/${personId}`),
    // 加入申请 / 审核
    applyJoin: (data) => api.post('/family/apply', data),
    getApplications: (familyId, userId, status) => api.get('/family/applications', { params: { familyId, userId, status } }),
    reviewApplication: (data) => api.post('/family/applications/review', data),
    getMyApplication: (familyId, userId) => api.get('/family/myApplication', { params: { familyId, userId } }),
    getMyApplications: (userId) => api.get('/family/myApplications', { params: { userId } }),
    // 登录通知：待审核申请（管理者）+ 未读审核结果（申请人）
    getNotifications: (userId) => api.get('/family/notifications', { params: { userId } }),
    markNotificationsRead: (data) => api.post('/family/notifications/read', data),
    markPendingNotificationsRead: (data) => api.post('/family/notifications/pending-read', data)
  }
}
